import React from 'react';
import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartTheme } from '@modules/ui-components/hooks/useChartTheme';
import type { CreativeWatchOnlyPltv } from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { DataThroughCaption } from '../DataThroughCaption';
import { formatMinorMoney } from './creativeDecisionFormat';

interface BandPoint {
  date: string;
  /** [p10, p90] in major units; undefined when that day had no band. */
  band?: [number, number];
  p50?: number;
  /** Backtest-lane twins — separate dataKeys so the replayed series renders
   * visually distinct (dashed, lighter) and is never blended with earned. */
  bandBacktest?: [number, number];
  p50Backtest?: number;
  realized?: number;
}

/**
 * The honesty label for the calibration window (CFVS-S2C-07): earned and
 * backtest day counts are ALWAYS separate — never one summed number.
 */
export function formatWatchOnlyCalibrationLabel(liveDays: number, backtestDays: number): string {
  if (liveDays > 0 && backtestDays > 0) {
    return `calibration: earned (live, ${liveDays} days) · backtest (${backtestDays} days)`;
  }
  if (backtestDays > 0) return `calibration: backtest (${backtestDays} days)`;
  if (liveDays > 0) return `calibration: earned (live, ${liveDays} days)`;
  return 'calibration: no snapshot days yet';
}

/**
 * Watch-only pLTV band dynamics (per-creative brief I2). Renders the
 * append-only snapshot history AS SERVED — one producer (the snapshot
 * writer), this surface never computes an estimate. The band is a FORECAST
 * on the gross AUDIT basis and is deliberately lighter than realized money;
 * it never feeds kill/scale (structural contract ban).
 */
export function CreativeWatchOnlyPltvSection({
  watchOnly,
}: {
  watchOnly?: CreativeWatchOnlyPltv;
}): React.ReactElement | null {
  const chart = useChartTheme();
  if (!watchOnly) return null;

  const latest = watchOnly.latest;
  const exponent = latest?.currencyExponent ?? 2;
  const currency = latest?.originalCurrency ?? 'USD';
  const toMajor = (minor: number): number => minor / (10 ** exponent);
  const points: BandPoint[] = watchOnly.points.map((point) => {
    const isBacktest = point.lane === 'backtest';
    return {
      date: point.snapshotDate,
      ...(point.p10Minor !== undefined && point.p90Minor !== undefined
        ? {
            [isBacktest ? 'bandBacktest' : 'band']:
              [toMajor(point.p10Minor), toMajor(point.p90Minor)] as [number, number],
          }
        : {}),
      ...(point.p50Minor !== undefined
        ? { [isBacktest ? 'p50Backtest' : 'p50']: toMajor(point.p50Minor) }
        : {}),
      ...(point.realizedGrossToDateMinor !== undefined && point.ownSubscriptions > 0
        ? { realized: toMajor(Math.round(point.realizedGrossToDateMinor / point.ownSubscriptions)) }
        : {}),
    };
  });
  const liveDays = watchOnly.liveDays ?? watchOnly.points.filter((point) => point.lane !== 'backtest').length;
  const backtestDays = watchOnly.backtestDays
    ?? watchOnly.points.length - (watchOnly.liveDays ?? watchOnly.points.filter((point) => point.lane !== 'backtest').length);
  const hasBacktest = backtestDays > 0;
  const dataThrough = watchOnly.points[watchOnly.points.length - 1]?.snapshotDate;

  return (
    <Box data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWatchOnlyPltv} sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Watch-only pLTV</Typography>
        <Chip
          size="small"
          variant="outlined"
          color="info"
          label="watch-only"
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWatchOnlyPltvState}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {latest
          ? `12-month forecast per subscriber · never a kill/scale input · prior: ${latest.priorScope}${
            latest.priorShare !== undefined ? ` (${Math.round(latest.priorShare * 100)}% weight)` : ''
          } · n=${latest.ownSubscriptions}`
          : 'never a kill/scale input'}
      </Typography>

      {watchOnly.cadenceSignal && (
        // D2 split (C2-AC-005/006): TWO chips from TWO independent fields —
        // an early weekly forecast can be watch_only while the D60 outcome
        // simultaneously reads not mature. Never derived from one string.
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
          <Chip
            size="small"
            variant="outlined"
            color={watchOnly.cadenceSignal.signalState === 'watch_only' ? 'info'
              : watchOnly.cadenceSignal.signalState === 'decision_grade' ? 'success' : 'default'}
            label={`signal: ${watchOnly.cadenceSignal.signalState.replace('_', '-')}${
              watchOnly.cadenceSignal.observedBillingOpportunities !== null
                ? ` · ${watchOnly.cadenceSignal.observedBillingOpportunities} billing ${
                  watchOnly.cadenceSignal.observedBillingOpportunities === 1 ? 'opportunity' : 'opportunities'
                } (${watchOnly.cadenceSignal.cadenceClass})`
                : ` · ${watchOnly.cadenceSignal.cadenceClass}`}`}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerSignalState}
          />
          <Chip
            size="small"
            variant="outlined"
            color={watchOnly.cadenceSignal.targetMaturityState === 'mature' ? 'success' : 'warning'}
            label={watchOnly.cadenceSignal.targetMaturityState === 'mature'
              ? `D${watchOnly.cadenceSignal.targetHorizonDays} outcome mature`
              : `D${watchOnly.cadenceSignal.targetHorizonDays} outcome not mature`}
            data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerTargetMaturityState}
          />
        </Stack>
      )}

      {watchOnly.state === 'unavailable' && (
        <Alert severity="info">
          {`Watch-only pLTV is unavailable for this deployment (${watchOnly.reasonCodes.join(', ') || 'no snapshots yet'}).`}
        </Alert>
      )}
      {watchOnly.state === 'not_decision_grade' && (
        <Alert severity="info" icon={false} sx={{ fontStyle: 'italic' }}>
          {watchOnly.notDecisionGradeLabel ?? 'not decision-grade yet'}
        </Alert>
      )}

      {points.length > 0 && watchOnly.state === 'available' && (
        <>
          <Box
            role="img"
            aria-label={`Watch-only pLTV band dynamics over ${points.length} days; latest P50 ${
              latest ? formatMinorMoney(latest.p50Minor, currency, exponent) : 'unavailable'
            }`}
            sx={{ height: 220 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={chart.axisTick} />
                <YAxis tick={chart.axisTick} width={56} />
                <RechartsTooltip
                  cursor={{ fill: chart.cursorFill }}
                  formatter={(value, name) => (
                    Array.isArray(value)
                      ? [`${Number(value[0]).toFixed(2)}–${Number(value[1]).toFixed(2)} ${currency}`, name]
                      : [`${Number(value).toFixed(2)} ${currency}`, name]
                  )}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {hasBacktest && (
                  <Area
                    name="P10–P90 band (backtest replay)"
                    dataKey="bandBacktest"
                    stroke={chart.primary}
                    strokeDasharray="4 3"
                    fill={chart.primary}
                    fillOpacity={0.06}
                    strokeWidth={1}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )}
                {hasBacktest && (
                  <Line
                    name="P50 (backtest replay)"
                    dataKey="p50Backtest"
                    stroke={chart.primary}
                    strokeDasharray="4 3"
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )}
                <Area
                  name={hasBacktest ? 'P10–P90 band (earned)' : 'P10–P90 forecast band'}
                  dataKey="band"
                  stroke={chart.primary}
                  fill={chart.primary}
                  fillOpacity={0.14}
                  strokeWidth={1}
                  isAnimationActive={false}
                  connectNulls={false}
                />
                <Line
                  name={hasBacktest ? 'P50 (earned)' : 'P50 forecast'}
                  dataKey="p50"
                  stroke={chart.primary}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
                <Line
                  name="realized to date (factual)"
                  dataKey="realized"
                  stroke={chart.reference}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
          <Box component="ol" role="list" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', m: 0, p: 0 }}>
            {watchOnly.points.map((point) => (
              <li
                key={point.snapshotDate}
                data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWatchOnlyPltvDay(point.snapshotDate)}
              >
                {`${point.snapshotDate}: ${point.estimateState}${
                  point.p50Minor !== undefined
                    ? `, P50 ${formatMinorMoney(point.p50Minor, currency, exponent)}`
                    : ''
                }, n=${point.ownSubscriptions}${point.lane === 'backtest' ? ' · backtest' : ''}`}
              </li>
            ))}
          </Box>
        </>
      )}
      <DataThroughCaption dataThrough={dataThrough} testId={`${CREATIVE_DECISIONS_TEST_IDS.drawerWatchOnlyPltv}-through`} />
      {watchOnly.points.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerWatchOnlyPltvCalibration}
        >
          {formatWatchOnlyCalibrationLabel(liveDays, backtestDays)}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        snapshots daily, never restated · gross basis (audit context)
      </Typography>
    </Box>
  );
}
